import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

import { HEARTH_MODELS, labelForModelId } from "../../../../shared/models";
import { setModel, setModelPicker } from "../../../model/api";
import { useAgentModel, useModelPicker } from "../../../model/use-agent-model";

export const Route = createFileRoute("/_workspace/settings/model")({
  component: ModelPane,
});

/** Label for a stored id; falls back to the raw id for an unknown/legacy one. */
function modelLabel(id: string | undefined): string {
  if (!id) return "";
  return labelForModelId(id);
}

function ModelPane() {
  const qc = useQueryClient();
  const current = useAgentModel();

  // Local override of the saved value; until the user picks something, the
  // select reflects whatever is saved.
  const [selected, setSelected] = useState<string | null>(null);
  const value = selected ?? current.data;

  const save = useMutation({
    mutationFn: (id: string) => setModel(id),
    onSuccess: (saved) => {
      qc.setQueryData(["model"], saved);
      setSelected(null);
    },
  });

  const dirty = value !== undefined && value !== current.data;

  return (
    <div className="mx-auto w-full max-w-2xl px-6 pt-8 pb-12">
      <h1 className="text-lg font-semibold">Model</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        The model Agent Heath uses for new conversations in this household.
        Existing conversations keep the model they started with.
      </p>
      <p className="mt-2 text-sm text-muted-foreground">
        Some models let you set how hard Heath thinks — a menu next to the
        message box. It applies to that whole conversation until you change it
        again. Models without one run at a fixed setting.
      </p>

      <section className="mt-6">
        {current.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : current.error ? (
          <p className="text-sm text-destructive">
            {(current.error as Error).message}
          </p>
        ) : (
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (dirty && value) save.mutate(value);
            }}
          >
            <Select
              value={value}
              onValueChange={setSelected}
              disabled={save.isPending}
            >
              <SelectTrigger
                aria-label="Model"
                className="w-full max-w-sm font-normal"
              >
                <SelectValue placeholder="Select a model">
                  {modelLabel(value)}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {HEARTH_MODELS.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {save.error ? (
              <p className="text-sm text-destructive">
                {(save.error as Error).message}
              </p>
            ) : null}

            <div>
              <Button type="submit" disabled={!dirty || save.isPending}>
                {save.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        )}
      </section>

      <ModelPickerToggle />
    </div>
  );
}

/**
 * Yours alone, not the household's: it only decides whether YOU see the menu.
 * Saves on toggle: there's nothing to review before committing a switch.
 */
function ModelPickerToggle() {
  const qc = useQueryClient();
  const picker = useModelPicker();

  const save = useMutation({
    mutationFn: (enabled: boolean) => setModelPicker(enabled),
    onSuccess: (enabled) => qc.setQueryData(["model-picker"], enabled),
  });

  return (
    <section className="mt-10 border-t pt-6">
      <div className="flex items-start justify-between gap-6">
        <div>
          <h2 className="text-sm font-medium">Pick a model per chat</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Show a model menu next to your message box, so you can start a
            conversation on a model other than the one above. This is your own
            setting, and it doesn't change what anyone else sees.
          </p>
        </div>
        <Switch
          aria-label="Show a model menu in the composer"
          checked={picker.data ?? false}
          disabled={picker.isLoading || save.isPending}
          onCheckedChange={(enabled) => save.mutate(enabled)}
        />
      </div>
      {save.error ? (
        <p className="mt-3 text-sm text-destructive">
          {(save.error as Error).message}
        </p>
      ) : null}
    </section>
  );
}
